import { AuthSessionResponseSchema } from "@cross-border-agent/contracts";
import { SessionBridgeResponseSchema, sessionChallenge, type SessionBridgeRequest, type SessionBridgeResult } from "@cross-border-agent/contracts/session-sync";
import { API_BASE, api, loadSession, saveSession, loadSyncMeta, SYNC_META_KEY, type StoredSession } from "./api";

type Action = SessionBridgeRequest extends infer T ? T extends SessionBridgeRequest ? Omit<T, "source" | "version" | "id" | "apiOrigin"> : never : never;
const OUTBOX = "xiaomanWebSessionRevocations";
const LOGOUT_PENDING = "xiaomanWebSessionLogoutPending";
const CONFLICT = "网页与插件登录了不同账号，已暂停同步。请退出其中一端后，用希望保留的账号重新登录；不会自动覆盖现有账号。";
let lastState: Extract<SessionBridgeResult, { kind: "status" }> | undefined;

export function createSessionBridgeClient(target: Window = window) {
  let disposed = false;
  const pending = new Map<string, { resolve: (value: SessionBridgeResult) => void; reject: (reason: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  const receive = (event: MessageEvent) => {
    if (event.source !== target || event.origin !== target.location.origin) return;
    const parsed = SessionBridgeResponseSchema.safeParse(event.data);
    if (!parsed.success) return;
    const item = pending.get(parsed.data.id);
    if (!item) return;
    clearTimeout(item.timer); pending.delete(parsed.data.id); item.resolve(parsed.data.result);
  };
  target.addEventListener("message", receive);
  return {
    request(action: Action): Promise<SessionBridgeResult> {
      if (disposed) return Promise.reject(new Error("DISPOSED"));
      return new Promise((resolve, reject) => {
        const id = crypto.randomUUID();
        const timer = setTimeout(() => { pending.delete(id); reject(new Error("NO_EXTENSION")); }, action.action === "status" ? 2500 : 25000);
        pending.set(id, { resolve, reject, timer });
        target.postMessage({ source: "xiaoman-web-session", version: 1, id, apiOrigin: new URL(API_BASE).origin, ...action }, target.location.origin);
      });
    },
    dispose() { disposed = true; target.removeEventListener("message", receive); for (const entry of pending.values()) { clearTimeout(entry.timer); entry.reject(new Error("DISPOSED")); } pending.clear(); },
  };
}

function rememberExtension(id: string) {
  const current = loadSyncMeta();
  if (current && current.seenExtension !== id) localStorage.setItem(SYNC_META_KEY, JSON.stringify({ ...current, seenExtension: id }));
}
function queueRevocation(session: StoredSession) {
  const existing: string[] = JSON.parse(localStorage.getItem(OUTBOX) || "[]");
  localStorage.setItem(OUTBOX, JSON.stringify([...new Set([...existing, session.tokens.accessToken])].slice(-10)));
}
async function flushRevocations() {
  const run = async () => {
    const pending: string[] = JSON.parse(localStorage.getItem(OUTBOX) || "[]");
    for (const token of pending) {
      try {
        const response = await fetch(`${API_BASE}/api/v1/auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(5000), redirect: "error" });
        if (response.ok) {
          const latest: string[] = JSON.parse(localStorage.getItem(OUTBOX) || "[]");
          localStorage.setItem(OUTBOX, JSON.stringify(latest.filter(item => item !== token)));
        }
      } catch { /* Retry on focus / online, without losing the explicit logout marker. */ }
    }
  };
  return navigator.locks ? navigator.locks.request("xiaoman-web-session-revoke", run) : run();
}

export function logoutSharedWebSession() {
  const current = loadSession();
  if (current) {
    queueRevocation(current);
    // Bind a delayed logout to the observed extension event, never a future login.
    localStorage.setItem(LOGOUT_PENDING, JSON.stringify({ userId: current.user.id, expectedState: lastState?.state?.id || (lastState ? "legacy" : loadSyncMeta()?.seenExtension) }));
  }
  saveSession(null, "logout");
  if (lastState) rememberExtension(lastState.state?.id || "legacy");
  void flushRevocations();
}

export function startSessionSync(notice: (value: string) => void): () => void {
  const bridge = createSessionBridgeClient();
  let active = true, running = false, again = false;
  const synchronize = async () => {
    const before = loadSession(), meta = loadSyncMeta(), epoch = meta?.id;
    const unchanged = () => active && loadSyncMeta()?.id === epoch && loadSession()?.user.id === before?.user.id;
    const status = await bridge.request({ action: "status" });
    if (!unchanged()) return;
    if (status.kind === "error") { if (status.code === "ENVIRONMENT_MISMATCH") notice("网页与插件连接了不同服务环境，已暂停登录同步。请使用匹配版本的插件。"); return; }
    if (status.kind !== "status") return;
    lastState = status;
    const eventId = status.state?.id || "legacy";
    const explicit = meta?.kind === "login" && (meta.at || 0) > (status.state?.at || 0);
    const pendingLogout = JSON.parse(localStorage.getItem(LOGOUT_PENDING) || "null") as { userId: string; expectedState?: string } | null;
    if (pendingLogout) {
      const linkedWhileLoggingOut = status.state?.kind === "sync" && status.state.previousId === pendingLogout.expectedState;
      if (pendingLogout.expectedState && status.userId === pendingLogout.userId && (pendingLogout.expectedState === eventId || linkedWhileLoggingOut)) {
        const done = await bridge.request({ action: "logout", ...pendingLogout, expectedState: pendingLogout.expectedState });
        if (done.kind !== "ok") return;
      }
      // If no bridge was observable at logout, do not kill a potentially newer login.
      localStorage.removeItem(LOGOUT_PENDING);
      rememberExtension(eventId);
      return;
    }
    if (before && status.userId && before.user.id !== status.userId) { notice(CONFLICT); rememberExtension(eventId); return; }
    if (before && !status.userId && status.state?.kind === "logout" && status.state.userId === before.user.id && !explicit) {
      queueRevocation(before); saveSession(null, "logout"); rememberExtension(eventId); void flushRevocations(); notice(""); return;
    }
    if (!before && meta?.kind === "logout" && (meta.seenExtension === eventId || status.state?.kind !== "login" || !meta.seenExtension)) { rememberExtension(eventId); notice(""); return; }
    if (!before && !status.userId) { notice(""); return; }
    if (before?.sessionFamilyId && before.sessionFamilyId === status.familyId) { rememberExtension(eventId); notice(""); return; }
    if (status.userId) {
      const verifier = crypto.randomUUID();
      const offered = await bridge.request({ action: "offer", challenge: await sessionChallenge(verifier), expectedState: eventId, ...(before ? { userId: before.user.id } : {}) });
      if (!unchanged() || offered.kind !== "ticket") return;
      const next = AuthSessionResponseSchema.parse(await api.exchangeSession(offered.ticket, verifier));
      if (!unchanged()) return;
      // Check the source is still current before publishing a delayed HTTP response.
      const confirmed = await bridge.request({ action: "status" });
      if (!unchanged() || confirmed.kind !== "status" || confirmed.userId !== next.user.id || (confirmed.state?.id || "legacy") !== eventId) return;
      saveSession(next, "sync"); rememberExtension(eventId); notice("");
    } else if (before) {
      const prepared = await bridge.request({ action: "prepare", userId: before.user.id, explicit });
      if (!unchanged()) return;
      if (prepared.kind === "error" && prepared.code === "ACCOUNT_CONFLICT") { notice(CONFLICT); return; }
      if (prepared.kind !== "prepared") return;
      const ticket = await api.sessionTicket(prepared.challenge);
      if (!unchanged()) return;
      saveSession({ ...loadSession()!, sessionFamilyId: ticket.familyId }, "refresh");
      const accepted = await bridge.request({ action: "accept", nonce: prepared.nonce, ticket: ticket.ticket });
      if (accepted.kind === "ok" && unchanged()) { saveSession(loadSession(), "sync"); notice(""); }
    }
  };
  const trigger = () => {
    if (!active) return;
    if (running) { again = true; return; }
    running = true;
    const run = () => active ? synchronize() : Promise.resolve();
    void (navigator.locks ? navigator.locks.request("xiaoman-web-session-sync", run) : run())
      .catch(error => { if (active && error instanceof Error && error.message !== "NO_EXTENSION" && error.message !== "DISPOSED") notice("登录同步暂未完成，已有登录已保留。网络恢复后会自动重试。"); })
      .finally(() => { running = false; if (active && again) { again = false; trigger(); } });
  };
  const hint = (event: MessageEvent) => { if (event.source === window && event.origin === location.origin && event.data?.source === "xiaoman-session-hint" && event.data.version === 1) trigger(); };
  const retry = () => { void flushRevocations(); trigger(); };
  const timers = [setTimeout(trigger, 100), setTimeout(trigger, 2000)];
  const interval = setInterval(trigger, 15000);
  window.addEventListener("message", hint);
  for (const event of ["agent-session-changed", "storage", "focus", "online"]) window.addEventListener(event, retry);
  void flushRevocations();
  return () => { active = false; timers.forEach(clearTimeout); clearInterval(interval); bridge.dispose(); window.removeEventListener("message", hint); for (const event of ["agent-session-changed", "storage", "focus", "online"]) window.removeEventListener(event, retry); };
}
